<template>
  <ChartLayout
    title="Historical data and forecasts"
    half
    :units="units"
    palette="default-highlight"
  >
    <template #params>
      <ControlGroup>
        <ControlGroup class="case-select">
          <RadioGroup
            v-if="allowCaseSelection"
            :options="[
              { label: 'Main case', value: 'MAIN' },
              { label: 'Accelerated case', value: 'ACC' },
              { label: 'Compare cases', value: 'BOTH' },
            ]"
            v-model="selectedCase"
          />
        </ControlGroup>
        <ControlGroup v-if="selectedFlow === 'Capacity' && targetText">
          <div class="target-text">
            <p class="f-ui-2-bold">
              <!--
              <svg width="10" height="10" class="chart-legendItem--label">
                <circle
                  cx="5"
                  cy="5"
                  r="4"
                  stroke-width="0.5"
                  class="highcharts-point"
                  :style="{ fill: '#04f' }"
                />
              </svg>
              -->
              2030 renewable energy capacity ambition note
            </p>
            <div class="f-ui-2" v-html="targetText" />
          </div>
        </ControlGroup>
      </ControlGroup>
    </template>
    <DataSlicer
      :data="processedData"
      xAxisDimension="year"
      yAxisDimension="product"
      valueDimension="value"
      v-slot="{ slicedData, xAxisCategories }"
      :childElementType="null"
    >
      <Chart
        :key="`flowByYear${selectedFlow}`"
        :options="
          buildOptions(
            {
              chart: {
                height: 400,
              },
              series: slicedData ? formatSeries(slicedData) : [],
              plotOptions: {
                column: {
                  // groupPadding: 0.05,
                  pointPadding: 0,
                },
              },
              yAxis: {
                max: getMax(data, xAxisCategories),
                reversedStacks: true,
                plotLines: showTripling
                  ? [
                      {
                        value: 11000,
                        className: 'highcharts-dotted',
                        zIndex: 99,
                        label: {
                          text: 'COP28 goal of tripling global renewable capacity by 2030',
                        },
                      },
                      {
                        value: 0,
                        className: 'highcharts-zero-axis',
                        zIndex: 99,
                      },
                    ]
                  : [
                      {
                        value: 0,
                        className: 'highcharts-zero-axis',
                        zIndex: 99,
                      },
                    ],
              },
              xAxis: {
                plotBands: [
                  {
                    from: projectionYear - 0.5,
                    to: 2050,
                    label: {
                      align: 'left',
                      text:
                        selectedCase === 'BOTH'
                          ? 'Forecast<br>Main vs.<br>accelerated case'
                          : `Forecast<br>${
                              selectedCase === 'ACC' ? 'Accelerated' : 'Main'
                            } case`,
                    },
                  },
                ],
              },
              tooltip: {
                valueSuffix: ` ${units}`,
              },
              legend: {
                enabled: true,
                reversed: true,
                align: 'right',
                verticalAlign: 'middle',
                layout: 'vertical',
                width: '20%',
                floating: false,
              },
            },
            'column'
          )
        "
        :updateArgs="[true, true, true]"
      />
    </DataSlicer>
    <!--
    <div>
            <p class="f-ui-2-bold">
              <svg width="10" height="10" class="chart-legendItem--label">
                <circle
                  cx="5"
                  cy="5"
                  r="4"
                  stroke-width="0.5"
                  class="highcharts-point"
                  :style="{ fill: '#04f' }"
                />
              </svg>
              2030 total renewables ambition
            </p>
            <div class="f-ui-2" v-html="targetText" />
          </div> 
          -->
    <template #notes v-if="note">
      {{ note }}
    </template>
  </ChartLayout>
</template>

<script>
import {
  DataSlicer,
  ControlGroup,
  RadioGroup,
} from "@international-energy-agency/vue-ui";
import { Chart } from "highcharts-vue";
import ChartOptions, {
  ChartLayout,
} from "@international-energy-agency/vue-ui/highcharts";
export default {
  components: {
    DataSlicer,
    ControlGroup,
    RadioGroup,
    ChartLayout,
    Chart,
  },
  props: {
    products: {
      type: Array,
      required: true,
    },
    selectedFlow: {
      type: String,
      required: true,
    },
    allowCaseSelection: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Array,
      required: true,
    },
    targetText: {
      type: String,
      required: false,
    },
    note: {
      type: String,
      default: null,
    },
    isWorld: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      projectionYear: 2023,
      selectedCase: /* this.allowCaseSelection ? "BOTH" : */ "MAIN",
    };
  },
  watch: {
    allowCaseSelection(newValue, oldValue) {
      if (newValue && newValue !== oldValue) {
        this.selectedCase = "BOTH";
      } else if (!newValue && newValue !== oldValue) {
        this.selectedCase = "MAIN";
      }
    },
  },
  computed: {
    showTripling() {
      const { isWorld, selectedFlow, target } = this;
      return isWorld && selectedFlow === "Capacity" && target;
    },
    units() {
      const { processedData } = this;
      return processedData ? processedData[0]?.unit : "";
    },
    target() {
      const { data } = this;
      return data.find((r) => r.case === "Target");
    },
    processedData() {
      const { data, products, selectedFlow, selectedCase } = this;
      return data
        .sort((a, b) => (a.case > b.case ? -1 : 1))
        .reduce((filtered, r) => {
          if (r.case === "Target") return filtered;
          if (
            (selectedCase === "BOTH" || r.case === selectedCase) &&
            r.flow === selectedFlow &&
            products.includes(r.product)
          ) {
            filtered.push(
              selectedCase !== "BOTH" || r.case === "MAIN"
                ? r
                : { ...r, product: `${r.product}_${r.case}` }
            );
          }
          return filtered;
        }, []);
    },
  },
  methods: {
    buildOptions: ChartOptions.buildOptions,
    formatSeries(slicedData) {
      const { products, selectedCase, projectionYear, target, selectedFlow } =
        this;
      const timeSeries = slicedData
        .toSorted(
          (a, b) =>
            products.indexOf(b.name.replace("_ACC", "")) -
            products.indexOf(a.name.replace("_ACC", ""))
        )
        .map((series) => {
          const product = series.name.replace("_ACC", "");
          const isAcc = series.name.includes("_ACC");
          return {
            ...series,
            name: isAcc
              ? `${series.name.replace("_ACC", "")} (accelerated case)`
              : series.name,
            animationLimit: Infinity,
            id: series.name,
            colorIndex: products.indexOf(product) + 1,
            legendIndex: products.indexOf(product),
            linkedTo: isAcc ? product : null,
            data:
              selectedCase === "BOTH" && isAcc
                ? series.data.filter((r) => r[0] >= projectionYear)
                : series.data,
            stack: isAcc ? "acc" : "main",
            centerInCategory: true,
          };
        });
      if (target && selectedFlow === "Capacity") {
        timeSeries.push({
          name: `${target.year} total renewables ambition`,
          id: "target",
          data: [[target.year, target.value]],
          type: "column",
          centerInCategory: true,
          colorIndex: 0,
          stack: "main",
          showInLegend: true,
          legendIndex: 100,
          /*
          marker: {
            enabled: true,
            symbol: "diamond",
          },
          tooltip: {
            pointFormat: "<b>{point.y:.0f} GW</b>",
          },
          
          */
        });
      }
      return timeSeries;
    },
    getMax(data, years) {
      const { products, target, showTripling, selectedFlow } = this;
      if (!data || !years) return undefined;
      if (showTripling) return 12000;
      const hasAccelerated = data.some(
        (r) => r.flow === selectedFlow && r.case === "ACC"
      );
      const filteredData = data.filter(
        (r) =>
          r.flow === selectedFlow &&
          r.case === (hasAccelerated ? "ACC" : "MAIN") &&
          products.includes(r.product)
      );
      const yearMax = years.reduce((max, year) => {
        const yearSum = filteredData
          .filter((r) => r.year === year)
          .reduce((sum, r) => sum + r.value, 0);
        return yearSum > max ? yearSum : max;
      }, 0);
      return (
        Math.ceil(
          (selectedFlow === "Capacity" && target && target.value > yearMax
            ? target.value
            : yearMax) / 10
        ) * 10
      );
    },
  },
};
</script>

<style scoped>
.target-text {
  max-width: 40vw;
}
.control-group.case-select {
  align-self: flex-start;
  min-height: 36px;
}
</style>